import React, { useState, useEffect, useContext } from "react";
import { IoIosAddCircleOutline } from "react-icons/io";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { BaseURL } from "../../../../Files/BaseURL";
import AlertContext from "../../../../Context/Alert/AlertContext";
import ThemeContext from "../../../../Context/theme/ThemeContext";

const ProfileCreation = () => {
  const { theme } = useContext(ThemeContext);
  const [title, setTitle] = useState("");
  const [file, setFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const alertContext = useContext(AlertContext);
  const { showAlert } = alertContext;

  // Function to fetch portfolio data based on editid
  const fetchPortfolioData = async (editid) => {
    try {
      setIsFetching(true);
      const authToken = localStorage.getItem("p@token");
      const response = await axios.get(
        `${BaseURL}/api/portfolio/personal/fetchpersoanlportfolio/${editid}`,
        {
          headers: {
            "auth-token": authToken,
          },
        }
      );

      if (response.data) {
        const { Title, Images } = response.data;
        setTitle(Title || "");
        setImagePreview(Images || null);
      }
    } catch (error) {
      showAlert(
        error.response?.data?.message || "Error fetching portfolio data",
        "danger"
      );
    } finally {
      setIsFetching(false);
    }
  };

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const editid = searchParams.get("editid");

    if (editid) {
      fetchPortfolioData(editid);
    }
  }, [location]);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];

    // Validate file type
    const allowedTypes = ["image/jpeg", "image/png", "image/jpg"];
    if (!allowedTypes.includes(selectedFile?.type)) {
      showAlert("Please upload only JPG, JPEG or PNG images", "warning");
      return;
    }

    // Validate file size (5MB max)
    if (selectedFile?.size > 5 * 1024 * 1024) {
      showAlert("File size should be less than 5MB", "warning");
      return;
    }

    setFile(selectedFile);
    setImagePreview(URL.createObjectURL(selectedFile));
  };

  const handleNextClick = async () => {
    try {
      if (!title.trim()) {
        showAlert("Please enter a title", "warning");
        return;
      }

      if (!imagePreview && !file) {
        showAlert("Please upload an image", "warning");
        return;
      }

      setIsLoading(true);

      const searchParams = new URLSearchParams(location.search);
      const editid = searchParams.get("editid");

      const state = {
        title: title.trim(),
        file: file,
        imagePreview: imagePreview,
      };

      if (editid) {
        navigate(`/user-dashboard/profile/profile_overview?editid=${editid}`, {
          state,
        });
      } else {
        navigate("/user-dashboard/profile/profile_overview", { state });
      }
    } catch (error) {
      showAlert("Error processing request", "danger");
    } finally {
      setIsLoading(false);
    }
  };

  if (isFetching) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primarygreen"></div>
      </div>
    );
  }

  return (
    <div className="flex items-center font-pop flex-col">
      <div
        className={`rounded-2xl py-6 px-6 my-6 w-[95%] md:w-[70%] xl:w-[40%] flex flex-col gap-4
             ${theme == "dark" ? "bg-black text-white" : "bg-white"}`}
      >
        <h1
          className={`font-semibold text-xl text-center
                  ${theme == "dark" && "text-white"}`}
        >
          Create portfolio
        </h1>
        <div className="flex flex-col gap-4">
          <input
            type="text"
            id="title"
            name="title"
            placeholder="Give your project brief a title"
            className={`w-full px-6 border-2 border-transparent font-pop text-base rounded-full py-2 focus:border-primarygreen focus:outline-none
                         ${
                           theme == "dark"
                             ? "bg-black text-white border border-white"
                             : "bg-[#E8E8E8]"
                         }`}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={100}
          />
          <div className="flex items-center justify-center h-44 w-full">
            <label
              htmlFor="dropzone-file"
              className={`flex flex-col h-full items-center justify-center w-full border-2 border-primarygreen border-dashed rounded-lg cursor-pointer 
                            ${
                              theme == "dark"
                                ? "bg-black text-white"
                                : "bg-transparent"
                            }`}
            >
              {imagePreview ? (
                <img
                  src={imagePreview}
                  alt="Selected File"
                  className="h-full w-full object-cover rounded-lg"
                />
              ) : (
                <div className="flex flex-row gap-4 items-center justify-center py-3 text-primarygreen font-pop">
                  <IoIosAddCircleOutline className="text-2xl" />
                  <p className={`text-base ${theme == "dark" && "text-white"}`}>
                    Attach Image
                  </p>
                </div>
              )}
              <input
                id="dropzone-file"
                accept="image/jpeg,image/png,image/jpg"
                type="file"
                className="hidden"
                onChange={handleFileChange}
              />
            </label>
          </div>
          <button
            className={`my-4 px-2 md:px-6 bg-green-500 hover:bg-transparent font-medium font-pop text-sm md:text-lg py-2 md:py-3 border border-black-500 hover:border-green-500 rounded-full text-white
                            ${
                              theme === "dark"
                                ? "hover:text-white"
                                : "hover:text-black"
                            }
                            ${
                              isLoading
                                ? "opacity-50 cursor-not-allowed"
                                : "cursor-pointer"
                            }`}
            onClick={handleNextClick}
            disabled={isLoading}
          >
            {isLoading ? (
              <div className="flex items-center justify-center">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
              </div>
            ) : (
              "Next"
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProfileCreation;
